import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import { useAppTranslation } from "locale/useAppTranslation";
import { vehicleAuxiliaryPageRouteName } from "../list/index.page";
import {
  useVehicleAuxiliaryServiceV1GetVehicleAuxiliaryQuery,
  useVehicleAuxiliaryServiceV1UpdateVehicleAuxiliaryMutation,
} from "_services/modules/vehicle-auxiliary";
import { toast } from "react-toastify";
import { errorHandler } from "_services/errorHandler";

interface EditVehicleAuxiliaryFormValues {
  auxiliaryName: string;
}

const schema = yup.object({
  auxiliaryName: yup.string().required(),
}).required();

const useEditVehicleAuxiliaryForm = (id: string | undefined) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useAppTranslation("vehicleAuxiliary");

  const { data: detail, isLoading } = useVehicleAuxiliaryServiceV1GetVehicleAuxiliaryQuery(
    { id: id! },
    { skip: !id }
  );

  const [updateVehicleAuxiliary] = useVehicleAuxiliaryServiceV1UpdateVehicleAuxiliaryMutation();

  const {
    handleSubmit,
    formState: { errors },
    register,
    setError,
    reset,
  } = useForm<EditVehicleAuxiliaryFormValues>({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: {
      auxiliaryName: "",
    },
  });

  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [pendingData, setPendingData] = useState<EditVehicleAuxiliaryFormValues | null>(null);

  useEffect(() => {
    dispatch(
      setGlobalComponent({
        title: t("page-title-edit"),
        hasBackButton: true,
      })
    );
  }, [dispatch, t]);

  useEffect(() => {
    if (detail?.auxiliary_name) {
      reset({ auxiliaryName: detail.auxiliary_name });
    }
  }, [detail, reset]);

  const onSubmit = (data: EditVehicleAuxiliaryFormValues) => {
    setPendingData(data);
    setShowSaveModal(true);
  };

  const handleConfirmSave = async () => {
    if (!pendingData || !id) return;
    try {
      await updateVehicleAuxiliary({
        id,
        vehicleAuxiliaryServiceV1UpdateVehicleAuxiliaryBody: {
          auxiliary_name: pendingData.auxiliaryName,
        },
      }).unwrap();
      toast.success(t("toast-update-success"));
      setShowSaveModal(false);
      navigate(-1);
    } catch (error: any) {
      setShowSaveModal(false);
      if (error?.status === 409) {
        setError("auxiliaryName", { type: "validate", message: t("error-duplicate-exists") });
      } else {
        errorHandler(error);
      }
    }
  };

  const handleConfirmCancel = () => {
    setShowCancelModal(false);
    navigate(-1);
  };

  return {
    handleSubmit,
    errors,
    register,
    setError,
    showSaveModal,
    setShowSaveModal,
    showCancelModal,
    setShowCancelModal,
    onSubmit,
    handleConfirmSave,
    handleConfirmCancel,
    t,
  };
};

export default useEditVehicleAuxiliaryForm;
